import { useState, useEffect } from "react";
import FooterComponent from "../../navigation/footer/footerComponent";
import { createFinancialAid, updateFinancialAid } from "../../../models/firestoreService";
import { sendSlackMessageTracked } from "../../../models/notifications";

// ── Constants ─────────────────────────────────────────────────────────────────

const PROGRAMS = ["2027 Startup Sprint", "Founder Track", "Residency", "Not sure yet"];
const INCOME_RANGES = ["Under $30,000", "$30,000 – $60,000", "$60,000 – $100,000", "$100,000+", "Prefer not to say"];
const PROVIDERS = ["Parent / Guardian", "Self", "Other"];
const COVERAGE = ["I cannot cover any portion", "I can cover ~25%", "I can cover ~50%", "I can cover ~75%"];

// ── localStorage helpers ──────────────────────────────────────────────────────

const LS_KEY = "extern_financial_aid_draft";

function loadDraft() {
    try { return JSON.parse(localStorage.getItem(LS_KEY)) ?? {}; } catch { return {}; }
}

function saveDraft(patch) {
    try {
        const current = loadDraft();
        localStorage.setItem(LS_KEY, JSON.stringify({ ...current, ...patch }));
    } catch {}
}

// ── Validation ────────────────────────────────────────────────────────────────

function validateS1(d) {
    const errs = [];
    if (!d.fullName.trim()) errs.push("Full Name is required");
    if (!d.email.trim() || !/\S+@\S+\.\S+/.test(d.email)) errs.push("A valid Email Address is required");
    return errs;
}

function validateS2(d) {
    const errs = [];
    if (!d.incomeRange) errs.push("Please select a household income range");
    if (!d.householdSize.trim()) errs.push("Household size is required");
    if (!d.primaryProvider) errs.push("Please select the primary financial provider");
    if (!d.coverage) errs.push("Please select how much tuition you can cover");
    if (!d.whyAid.trim()) errs.push("Please tell us why you're applying for financial aid");
    return errs;
}

// ── Shared UI ─────────────────────────────────────────────────────────────────

const inputCls = "w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-800 bg-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors";

function Field({ label, required, hint, children }) {
    return (
        <div className="mb-5">
            <label className="block text-xs font-medium text-gray-600 mb-1.5">
                {label}{required && <span className="text-red-500 ml-0.5">*</span>}
            </label>
            {hint && <p className="text-xs text-gray-400 mb-1.5">{hint}</p>}
            {children}
        </div>
    );
}

function Errors({ errors }) {
    if (!errors.length) return null;
    return (
        <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg">
            <ul className="list-disc list-inside space-y-1">
                {errors.map((e, i) => <li key={i} className="text-sm text-red-600">{e}</li>)}
            </ul>
        </div>
    );
}

function NavButtons({ onBack, onNext, nextLabel = "Next →", saving }) {
    return (
        <div className="flex justify-between items-center mt-8 pt-6 border-t border-gray-100">
            {onBack
                ? <button onClick={onBack} className="px-5 py-2.5 rounded-lg border border-gray-300 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors">← Back</button>
                : <div />
            }
            <button
                onClick={onNext}
                disabled={saving}
                className="px-7 py-2.5 rounded-lg text-white text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-60"
                style={{ backgroundColor: "#3769B9" }}
            >
                {saving ? "Saving…" : nextLabel}
            </button>
        </div>
    );
}

function OptionCards({ options, selected, onChange, multi = false }) {
    const toggle = (opt) => {
        if (multi) {
            onChange(selected.includes(opt) ? selected.filter(o => o !== opt) : [...selected, opt]);
        } else {
            onChange(opt);
        }
    };
    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {options.map(opt => {
                const active = multi ? selected.includes(opt) : selected === opt;
                return (
                    <button
                        key={opt}
                        type="button"
                        onClick={() => toggle(opt)}
                        className="text-left px-4 py-3 rounded-xl border-2 text-sm transition-all"
                        style={{
                            borderColor: active ? "#3769B9" : "#e5e7eb",
                            backgroundColor: active ? "#f0f4fb" : "#fff",
                            color: active ? "#3769B9" : "#374151",
                            fontWeight: active ? 600 : 400,
                        }}
                    >
                        {opt}
                    </button>
                );
            })}
        </div>
    );
}

// ── Progress bar ──────────────────────────────────────────────────────────────

function ProgressBar({ step }) {
    const steps = ["Personal Info", "Financial Details", "Submit"];
    return (
        <div className="flex items-start mb-10">
            {steps.map((label, i) => {
                const n = i + 1;
                const done = step > n;
                const active = step === n;
                return (
                    <div key={n} className="flex items-start flex-1">
                        <div className="flex flex-col items-center w-full">
                            <div
                                className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-all"
                                style={{
                                    backgroundColor: done ? "#059669" : active ? "#3769B9" : "#f3f4f6",
                                    color: done || active ? "#fff" : "#9ca3af",
                                }}
                            >
                                {done ? "✓" : n}
                            </div>
                            <span className="text-xs mt-1.5 text-center hidden sm:block" style={{ color: active ? "#3769B9" : "#9ca3af", fontWeight: active ? 600 : 400 }}>
                                {label}
                            </span>
                        </div>
                        {i < steps.length - 1 && (
                            <div className="flex-1 h-px mt-4 mx-1" style={{ backgroundColor: step > n ? "#059669" : "#e5e7eb" }} />
                        )}
                    </div>
                );
            })}
        </div>
    );
}

// ── Submitted banner ──────────────────────────────────────────────────────────

function SubmittedBanner({ submittedAt, updated }) {
    return (
        <div
            className="rounded-xl px-5 py-4 mb-6 flex items-start gap-3 transition-all"
            style={{ backgroundColor: updated ? "#f0fdf4" : "#f0f4fb", border: `1px solid ${updated ? "#86efac" : "#d0dff5"}` }}
        >
            <span className="text-lg mt-0.5">{updated ? "✓" : "📋"}</span>
            <div>
                <p className="text-sm font-semibold text-gray-800">
                    {updated ? "Application updated!" : "Financial Aid Application Submitted"}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                    {updated
                        ? "Your changes have been saved."
                        : `Submitted${submittedAt ? ` on ${new Date(submittedAt).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}` : ""}. You can edit and re-submit any time below.`
                    }
                </p>
            </div>
        </div>
    );
}

// ── Section 1: Personal Info ──────────────────────────────────────────────────

function Section1({ data, set, onNext, saving }) {
    const [errors, setErrors] = useState([]);
    const f = (key) => (e) => set(prev => ({ ...prev, [key]: e.target.value }));

    const handleNext = () => {
        const errs = validateS1(data);
        if (errs.length) { setErrors(errs); window.scrollTo({ top: 0, behavior: "smooth" }); return; }
        setErrors([]);
        onNext();
    };

    return (
        <div>
            <h2 className="text-xl font-bold text-gray-900 mb-1">Financial Aid Application</h2>
            <p className="text-sm text-gray-500 mb-7 leading-relaxed">
                Extern Academy is committed to making this program accessible to all driven students, regardless of financial background. This application is confidential.
            </p>
            <Errors errors={errors} />
            <Field label="Full Name" required>
                <input className={inputCls} value={data.fullName} onChange={f("fullName")} placeholder="Your full name" />
            </Field>
            <Field label="Email Address" required hint="Use the same email as your program application if you've already submitted one.">
                <input className={inputCls} type="email" value={data.email} onChange={f("email")} placeholder="you@example.com" />
            </Field>
            <Field label="Which program are you interested in?">
                <OptionCards options={PROGRAMS} selected={data.program} onChange={(v) => set(prev => ({ ...prev, program: v }))} />
            </Field>
            <NavButtons onNext={handleNext} saving={saving} />
        </div>
    );
}

// ── Section 2: Financial Details ──────────────────────────────────────────────

function Section2({ data, set, onNext, onBack, saving }) {
    const [errors, setErrors] = useState([]);
    const f = (key) => (e) => set(prev => ({ ...prev, [key]: e.target.value }));

    const handleNext = () => {
        const errs = validateS2(data);
        if (errs.length) { setErrors(errs); window.scrollTo({ top: 0, behavior: "smooth" }); return; }
        setErrors([]);
        onNext();
    };

    return (
        <div>
            <h2 className="text-xl font-bold text-gray-900 mb-1">Financial Details</h2>
            <p className="text-sm text-gray-500 mb-7">All information is kept strictly confidential and used solely for aid decisions.</p>
            <Errors errors={errors} />
            <Field label="Household Annual Income Range" required>
                <OptionCards options={INCOME_RANGES} selected={data.incomeRange} onChange={(v) => set(prev => ({ ...prev, incomeRange: v }))} />
            </Field>
            <Field label="Number of People in Household" required>
                <input className={inputCls} value={data.householdSize} onChange={f("householdSize")} placeholder="e.g. 4" type="number" min="1" />
            </Field>
            <Field label="Primary Financial Provider" required>
                <OptionCards options={PROVIDERS} selected={data.primaryProvider} onChange={(v) => set(prev => ({ ...prev, primaryProvider: v }))} />
            </Field>
            <Field label="What portion of tuition are you able to cover?" required>
                <OptionCards options={COVERAGE} selected={data.coverage} onChange={(v) => set(prev => ({ ...prev, coverage: v }))} />
            </Field>
            <Field label="Why are you applying for financial aid?" required>
                <textarea className={`${inputCls} resize-none`} rows={5} value={data.whyAid} onChange={f("whyAid")} placeholder="Tell us about your situation and why this program matters to you." />
            </Field>
            <Field label="Any extenuating financial circumstances?" hint="Optional — share anything else you'd like us to know.">
                <textarea className={`${inputCls} resize-none`} rows={4} value={data.circumstances} onChange={f("circumstances")} placeholder="e.g. recent job loss, medical expenses, single-parent household…" />
            </Field>
            <NavButtons onBack={onBack} onNext={handleNext} saving={saving} />
        </div>
    );
}

// ── Section 3: Submit ─────────────────────────────────────────────────────────

function Section3({ onSubmit, onBack, saving, submitted }) {
    return (
        <div>
            <h2 className="text-xl font-bold text-gray-900 mb-1">{submitted ? "Review & Update" : "Ready to Submit"}</h2>
            <p className="text-sm text-gray-500 mb-8">{submitted ? "Make any changes above and click Update to save them." : "Review your application and submit when ready."}</p>
            <div className="rounded-xl p-6 mb-8" style={{ backgroundColor: "#f0f4fb", border: "1px solid #d0dff5" }}>
                <p className="text-sm font-semibold text-gray-800 mb-2">What happens next?</p>
                <p className="text-sm text-gray-600 leading-relaxed">
                    Our team will review your application and reach out to you directly at the email you provided. If you haven't submitted your program application yet, you can do so at{" "}
                    <a href="/apply" style={{ color: "#3769B9" }} className="font-semibold">externacademy.com/apply</a>.
                </p>
            </div>
            <NavButtons
                onBack={onBack}
                onNext={onSubmit}
                nextLabel={saving ? (submitted ? "Updating…" : "Submitting…") : (submitted ? "Update Application" : "Submit Application")}
                saving={saving}
            />
        </div>
    );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function MainFinancialAidComponent() {
    const draft = loadDraft();

    const [step, setStep] = useState(draft.step ?? 1);
    const [recordId, setRecordId] = useState(draft.recordId ?? null);
    const [submitted, setSubmitted] = useState(draft.submitted ?? false);
    const [submittedAt, setSubmittedAt] = useState(draft.submittedAt ?? null);
    const [updated, setUpdated] = useState(false);
    const [saving, setSaving] = useState(false);

    const [s1, setS1] = useState({ fullName: "", email: "", program: "", ...draft.s1 });
    const [s2, setS2] = useState({ incomeRange: "", householdSize: "", primaryProvider: "", coverage: "", whyAid: "", circumstances: "", ...draft.s2 });

    useEffect(() => { saveDraft({ step, recordId, submitted, submittedAt, s1, s2 }); }, [step, recordId, submitted, submittedAt, s1, s2]);

    const handleSection1 = async () => {
        setSaving(true);
        try {
            if (recordId) {
                await updateFinancialAid(recordId, {
                    "Full Name": s1.fullName,
                    "Email": s1.email,
                    "Program Interest": s1.program || null,
                    "Status": submitted ? "Complete" : "Section 1",
                });
            } else {
                const id = await createFinancialAid({
                    "Full Name": s1.fullName,
                    "Email": s1.email,
                    "Program Interest": s1.program || null,
                    "Status": "Section 1",
                });
                setRecordId(id);
                sendSlackMessageTracked(`New financial aid application started — ${s1.fullName} (${s1.email})`);
            }
        } catch (e) { console.error("Firestore save failed:", e); }
        setSaving(false);
        setStep(2);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const handleSection2 = async () => {
        setSaving(true);
        try {
            if (recordId) {
                await updateFinancialAid(recordId, {
                    "Income Range": s2.incomeRange,
                    "Household Size": s2.householdSize,
                    "Primary Provider": s2.primaryProvider,
                    "Tuition Coverage": s2.coverage,
                    "Why Financial Aid": s2.whyAid,
                    "Extenuating Circumstances": s2.circumstances || null,
                    "Status": submitted ? "Complete" : "Section 2",
                });
            }
        } catch (e) { console.error("Firestore save failed:", e); }
        setSaving(false);
        setStep(3);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const handleFinalSubmit = async () => {
        setSaving(true);
        try {
            if (recordId) {
                await updateFinancialAid(recordId, {
                    "Status": "Complete",
                    "officiallySubmitted": true,
                    ...(!submitted && { "submittedAt": new Date().toISOString() }),
                });
            }
            sendSlackMessageTracked(
                submitted
                    ? `Financial aid application updated — ${s1.fullName} (${s1.email})`
                    : `Financial aid application submitted — ${s1.fullName} (${s1.email})`
            );
            if (!submitted) setSubmittedAt(new Date().toISOString());
            setSubmitted(true);
            setUpdated(true);
            setTimeout(() => setUpdated(false), 3500);
        } catch (e) { console.error("Firestore save failed:", e); }
        setSaving(false);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    return (
        <div style={{ backgroundColor: "#f7f8fa", minHeight: "100vh" }}>
            <div className="max-w-3xl mx-auto px-4 py-12">
                {submitted && <SubmittedBanner submittedAt={submittedAt} updated={updated} />}
                <ProgressBar step={step} />
                <div className="bg-white rounded-2xl shadow-sm p-8">
                    {step === 1 && <Section1 data={s1} set={setS1} onNext={handleSection1} saving={saving} />}
                    {step === 2 && <Section2 data={s2} set={setS2} onNext={handleSection2} onBack={() => { setStep(1); window.scrollTo({ top: 0, behavior: "smooth" }); }} saving={saving} />}
                    {step === 3 && <Section3 onSubmit={handleFinalSubmit} onBack={() => { setStep(2); window.scrollTo({ top: 0, behavior: "smooth" }); }} saving={saving} submitted={submitted} />}
                </div>
            </div>
            <FooterComponent />
        </div>
    );
}
